/**
 * Options step component
 * Step 3 of job wizard - configuration options
 * Shows live map preview on the right that updates with settings changes
 */

import { useMemo, useCallback, useState } from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import { Form, Slider, Switch, Select, Collapse, Typography, Space, Card, Alert, Tooltip, Row, Col } from 'antd';
import { InfoCircleOutlined, SettingOutlined, ThunderboltOutlined, CheckOutlined } from '@ant-design/icons';
import { MapContainer } from '@/components/maps/MapContainer';
import { GridOverlay } from '@/components/maps/GridOverlay';
import type { Map as LeafletMap } from 'leaflet';
import type { CreateJobInput } from '@/types/job';
import type { GridConfig } from '@/types/grid';
import { calculateArea, generateGridPreview, zoomToCellSize } from '@/utils/mapUtils';
import { formatNumber } from '@/utils/formatters';
import { MAP_DEFAULTS } from '@/utils/constants';

const { Title, Text, Paragraph } = Typography;
const { Panel } = Collapse;

const ZOOM_LEVELS = [
  { value: 14, label: '14 - Very large cells (~100km²)', description: 'Fastest but may miss places in dense areas' },
  { value: 15, label: '15 - Large cells (~25km²)', description: 'Fast, good for suburban areas' },
  { value: 16, label: '16 - Medium cells (~4km²)', description: 'Recommended for most cases' },
  { value: 17, label: '17 - Small cells (~1km²)', description: 'Good for dense urban areas' },
  { value: 18, label: '18 - Very small cells (~0.25km²)', description: 'Most thorough, but slowest' },
];

const PRESETS = {
  fast: { zoomLevel: 14, maxResultsPerCell: 120, includeDetails: true },
  balanced: { zoomLevel: 16, maxResultsPerCell: 120, includeDetails: true },
  thorough: { zoomLevel: 18, maxResultsPerCell: 120, includeDetails: true },
} as const;

type PresetType = keyof typeof PRESETS;

export function OptionsStep() {
  const {
    control,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<CreateJobInput>();

  const [mapRef, setMapRef] = useState<LeafletMap | null>(null);

  const bounds = watch('bounds');
  const polygon = watch('polygon');
  const zoomLevel = watch('zoomLevel') || 16;
  const maxResultsPerCell = watch('maxResultsPerCell') || 120;
  const includeDetails = watch('includeDetails') ?? true;
  const gridOverlapPercent = watch('gridOverlapPercent') ?? 10;

  // Generate grid preview based on current settings
  const gridConfig: GridConfig = useMemo(() => ({
    zoomLevel,
    cellSizeKm: zoomToCellSize(zoomLevel),
    overlapPercent: gridOverlapPercent,
  }), [zoomLevel, gridOverlapPercent]);

  const gridPreview = useMemo(() => {
    if (!bounds) return null;
    return generateGridPreview(bounds, gridConfig, polygon);
  }, [bounds, gridConfig, polygon]);

  const areaInfo = useMemo(() => {
    if (!bounds || !gridPreview) return null;
    const areaKm2 = calculateArea(bounds);
    return {
      area: areaKm2,
      cells: gridPreview.totalCells,
      estimatedTime: Math.ceil((gridPreview.totalCells * 3) / 60),
    };
  }, [bounds, gridPreview]);

  // Detect which preset matches current settings (only zoom level matters now)
  const activePreset = useMemo((): PresetType | null => {
    for (const [key, preset] of Object.entries(PRESETS)) {
      if (preset.zoomLevel === zoomLevel) {
        return key as PresetType;
      }
    }
    return null;
  }, [zoomLevel]);

  const handlePresetChange = (preset: PresetType) => {
    const settings = PRESETS[preset];
    setValue('zoomLevel', settings.zoomLevel);
    setValue('maxResultsPerCell', settings.maxResultsPerCell);
    setValue('includeDetails', settings.includeDetails);
  };

  const handleMapReady = useCallback((map: LeafletMap) => {
    setMapRef(map);
    // Fit to bounds when map is ready
    if (bounds) {
      map.fitBounds([
        [bounds.south, bounds.west],
        [bounds.north, bounds.east],
      ], { padding: [20, 20] });
    }
  }, [bounds]);

  return (
    <div>
      <Title level={4} style={{ marginBottom: 8 }}>
        Configure scraping options
      </Title>
      <Paragraph type="secondary" style={{ marginBottom: 24 }}>
        Adjust settings to optimize speed and thoroughness. The map preview updates live.
      </Paragraph>

      <Row gutter={24}>
        {/* Left side - Options */}
        <Col xs={24} lg={12}>
          {/* Quick presets */}
          <Card size="small" style={{ marginBottom: 16 }}>
            <Text strong style={{ display: 'block', marginBottom: 12 }}>
              Quick Presets
            </Text>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {/* Fast preset */}
              <Card
                size="small"
                hoverable
                onClick={() => handlePresetChange('fast')}
                style={{
                  flex: '1 1 80px',
                  maxWidth: 120,
                  textAlign: 'center',
                  borderColor: activePreset === 'fast' ? '#52c41a' : undefined,
                  borderWidth: activePreset === 'fast' ? 2 : 1,
                  background: activePreset === 'fast' ? 'rgba(82, 196, 26, 0.1)' : undefined,
                }}
                styles={{ body: { padding: 8 } }}
              >
                <div style={{ position: 'relative' }}>
                  {activePreset === 'fast' && (
                    <CheckOutlined style={{ position: 'absolute', top: -4, right: -4, color: '#52c41a', fontSize: 14 }} />
                  )}
                  <ThunderboltOutlined style={{ fontSize: 20, color: '#52c41a' }} />
                  <div style={{ marginTop: 4 }}>
                    <Text strong style={{ fontSize: 12 }}>Fast</Text>
                  </div>
                </div>
              </Card>

              {/* Balanced preset */}
              <Card
                size="small"
                hoverable
                onClick={() => handlePresetChange('balanced')}
                style={{
                  flex: '1 1 80px',
                  maxWidth: 120,
                  textAlign: 'center',
                  borderColor: activePreset === 'balanced' ? '#1677ff' : undefined,
                  borderWidth: activePreset === 'balanced' ? 2 : 1,
                  background: activePreset === 'balanced' ? 'rgba(22, 119, 255, 0.1)' : undefined,
                }}
                styles={{ body: { padding: 8 } }}
              >
                <div style={{ position: 'relative' }}>
                  {activePreset === 'balanced' && (
                    <CheckOutlined style={{ position: 'absolute', top: -4, right: -4, color: '#1677ff', fontSize: 14 }} />
                  )}
                  <SettingOutlined style={{ fontSize: 20, color: '#1677ff' }} />
                  <div style={{ marginTop: 4 }}>
                    <Text strong style={{ fontSize: 12 }}>Balanced</Text>
                  </div>
                </div>
              </Card>

              {/* Thorough preset */}
              <Card
                size="small"
                hoverable
                onClick={() => handlePresetChange('thorough')}
                style={{
                  flex: '1 1 80px',
                  maxWidth: 120,
                  textAlign: 'center',
                  borderColor: activePreset === 'thorough' ? '#722ed1' : undefined,
                  borderWidth: activePreset === 'thorough' ? 2 : 1,
                  background: activePreset === 'thorough' ? 'rgba(114, 46, 209, 0.1)' : undefined,
                }}
                styles={{ body: { padding: 8 } }}
              >
                <div style={{ position: 'relative' }}>
                  {activePreset === 'thorough' && (
                    <CheckOutlined style={{ position: 'absolute', top: -4, right: -4, color: '#722ed1', fontSize: 14 }} />
                  )}
                  <InfoCircleOutlined style={{ fontSize: 20, color: '#722ed1' }} />
                  <div style={{ marginTop: 4 }}>
                    <Text strong style={{ fontSize: 12 }}>Thorough</Text>
                  </div>
                </div>
              </Card>
            </div>
          </Card>

          {/* Main options */}
          <Card size="small">
            <Form layout="vertical" style={{ marginBottom: 0 }}>
              {/* Scramble mode */}
              <Form.Item
                label={
                  <Space>
                    <span>Scramble Mode</span>
                    <Tooltip title="Process grid cells in random order for homogeneous coverage across the search area, even if the job is only partially completed.">
                      <InfoCircleOutlined style={{ color: 'var(--color-text-tertiary)' }} />
                    </Tooltip>
                  </Space>
                }
                style={{ marginBottom: 16 }}
              >
                <Controller
                  name="scrambleOrder"
                  control={control}
                  render={({ field }) => (
                    <Switch
                      checked={field.value ?? true}
                      onChange={field.onChange}
                      checkedChildren="On"
                      unCheckedChildren="Off"
                    />
                  )}
                />
                <Text type="secondary" style={{ display: 'block', marginTop: 4, fontSize: 11 }}>
                  Randomizes cell processing order instead of scanning left-to-right
                </Text>
              </Form.Item>

              {/* Zoom level */}
              <Form.Item
                label={
                  <Space>
                    <span>Grid Zoom Level</span>
                    <Tooltip title="Higher zoom = smaller cells = more thorough but slower">
                      <InfoCircleOutlined style={{ color: 'var(--color-text-tertiary)' }} />
                    </Tooltip>
                  </Space>
                }
                validateStatus={errors.zoomLevel ? 'error' : undefined}
                help={errors.zoomLevel?.message}
                style={{ marginBottom: 16 }}
              >
                <Controller
                  name="zoomLevel"
                  control={control}
                  render={({ field }) => {
                    const selectedZoom = ZOOM_LEVELS.find((z) => z.value === field.value);
                    return (
                      <>
                        <Select
                          {...field}
                          options={ZOOM_LEVELS.map((z) => ({
                            value: z.value,
                            label: z.label,
                          }))}
                          style={{ width: '100%' }}
                        />
                        {selectedZoom && (
                          <Text type="secondary" style={{ display: 'block', fontSize: 11, marginTop: 4 }}>
                            {selectedZoom.description}
                          </Text>
                        )}
                      </>
                    );
                  }}
                />
              </Form.Item>
            </Form>
          </Card>

          {/* Advanced options */}
          <Collapse ghost style={{ marginTop: 16 }}>
            <Panel header="Advanced Options" key="advanced">
              <Alert
                type="info"
                message="Advanced settings use sensible defaults."
                style={{ marginBottom: 12 }}
              />

              <Form layout="vertical">
                {/* Max results per cell */}
                <Form.Item
                  label={
                    <Space>
                      <span>Max Results Per Cell</span>
                      <Tooltip title="Google Maps limits results to ~120 per search. Lower values are faster.">
                        <InfoCircleOutlined style={{ color: 'var(--color-text-tertiary)' }} />
                      </Tooltip>
                    </Space>
                  }
                  style={{ marginBottom: 16 }}
                >
                  <Controller
                    name="maxResultsPerCell"
                    control={control}
                    render={({ field }) => (
                      <Slider
                        {...field}
                        min={20}
                        max={120}
                        step={20}
                        marks={{
                          20: '20',
                          60: '60',
                          120: '120',
                        }}
                      />
                    )}
                  />
                </Form.Item>

                {/* Include details */}
                <Form.Item
                  label={
                    <Space>
                      <span>Include Detailed Information</span>
                      <Tooltip title="Fetch additional details like phone, website, hours for each place.">
                        <InfoCircleOutlined style={{ color: 'var(--color-text-tertiary)' }} />
                      </Tooltip>
                    </Space>
                  }
                  style={{ marginBottom: 16 }}
                >
                  <Controller
                    name="includeDetails"
                    control={control}
                    render={({ field }) => (
                      <Switch
                        checked={field.value}
                        onChange={field.onChange}
                        checkedChildren="Yes"
                        unCheckedChildren="No"
                      />
                    )}
                  />
                  <Text type="secondary" style={{ display: 'block', marginTop: 4, fontSize: 11 }}>
                    Includes: phone, website, opening hours, reviews
                  </Text>
                </Form.Item>

                <Form.Item
                  label={
                    <Space>
                      <span>Grid Overlap Percentage</span>
                      <Tooltip title="Overlap between adjacent cells to catch places near edges. Higher values = more thorough but more duplicates.">
                        <InfoCircleOutlined style={{ color: 'var(--color-text-tertiary)' }} />
                      </Tooltip>
                    </Space>
                  }
                  style={{ marginBottom: 0 }}
                >
                  <Controller
                    name="gridOverlapPercent"
                    control={control}
                    render={({ field }) => (
                      <Slider
                        {...field}
                        min={0}
                        max={30}
                        step={5}
                        marks={{ 0: '0%', 10: '10%', 20: '20%', 30: '30%' }}
                      />
                    )}
                  />
                </Form.Item>
              </Form>
            </Panel>
          </Collapse>
        </Col>

        {/* Right side - Live map preview */}
        <Col xs={24} lg={12}>
          <Card
            title="Grid Preview"
            size="small"
            styles={{ body: { padding: 12 } }}
          >
            {bounds ? (
              <>
                <div style={{ position: 'relative', borderRadius: 8, overflow: 'hidden' }}>
                  <MapContainer
                    height={320}
                    center={MAP_DEFAULTS.center}
                    zoom={MAP_DEFAULTS.initialZoom}
                    onMapReady={handleMapReady}
                    scrollWheelZoom={true}
                    dragging={true}
                  >
                    {/* Show grid preview */}
                    {gridPreview && (
                      <GridOverlay bounds={bounds} gridPreview={gridPreview} />
                    )}
                  </MapContainer>
                </div>

                {/* Live stats */}
                {areaInfo && (
                  <div style={{ marginTop: 12 }}>
                    <Space size={24} style={{ width: '100%', justifyContent: 'center' }}>
                      <div style={{ textAlign: 'center' }}>
                        <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>Area</Text>
                        <Text strong>~{areaInfo.area.toFixed(1)} km²</Text>
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>Grid cells</Text>
                        <Text strong style={{ color: '#1677ff' }}>{formatNumber(areaInfo.cells)}</Text>
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>Est. time</Text>
                        <Text strong>~{areaInfo.estimatedTime} min</Text>
                      </div>
                    </Space>

                    {areaInfo.cells > 500 && (
                      <Alert
                        type="warning"
                        message="Large number of cells - consider using a lower zoom level for faster results"
                        style={{ marginTop: 12 }}
                        showIcon
                      />
                    )}
                  </div>
                )}
              </>
            ) : (
              <div
                style={{
                  height: 320,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'var(--color-bg-spotlight)',
                  borderRadius: 8,
                }}
              >
                <Text type="secondary">No area selected</Text>
              </div>
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
}

export default OptionsStep;
